import { useState, useEffect, useMemo, useRef } from "react";
import {
  Home,
  FileText,
  LayoutTemplate,
  Settings,
  BarChart3,
  CreditCard,
  Plus,
  Search,
  LogOut,
  Users,
  Folder,
  Tag,
  FileCode2,
  Key,
  Shield,
  ChevronsUpDown,
  Sparkles,
  BadgeCheck,
  Bell,
  Code2,
  Building2,
  Webhook,
  Send,
  Link,
  UserCheck,
  Library,
  FileEdit,
  BarChart2,
  Plug,
  MessageSquare,
  Crown,
  DollarSign,
  BookOpen,
  FileOutput,
  Loader2,
  Mail,
  ExternalLink,
  FileCode,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import logoOnDark from "@/assets/brand/logo-on-dark.svg";
import { LogoIcon } from "@/components/Logo";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar";
import { useLogout } from "@/hooks/useLogout";
import { useSidebar } from "@/hooks/useSidebar";
import { searchApi } from "@/services/api";
import { useAuthStore } from "@/store/auth.store";
import {
  filterNavItems,
  canAccessPage,
  getUserRoleDisplay,
} from "@/utils/permissions";

// Search Results Content Component (avoids nested ternary)
const SearchResultsContent = ({
  isSearching,
  hasSearchResults,
  searchResults,
  onSelect,
  t,
}) => {
  if (isSearching) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!hasSearchResults) {
    return (
      <div className="p-3 text-center">
        <p className="text-sm text-muted-foreground">
          {t("common.noResults", "No results found")}
        </p>
      </div>
    );
  }

  return (
    <div className="p-1">
      {/* Envelopes */}
      {searchResults?.envelopes?.length > 0 && (
        <div className="mb-2">
          <p className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t("nav.envelopes", "Envelopes")}
          </p>
          {searchResults.envelopes.map((item) => (
            <button
              key={`env-${item.id}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect("envelope", item);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              <FileText className="size-4 text-muted-foreground shrink-0" />
              <span className="truncate">{item.title}</span>
              <Badge variant="outline" className="ml-auto text-[10px] shrink-0">
                {item.status}
              </Badge>
            </button>
          ))}
        </div>
      )}

      {/* Templates */}
      {searchResults?.templates?.length > 0 && (
        <div className="mb-2">
          <p className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t("nav.templates", "Templates")}
          </p>
          {searchResults.templates.map((item) => (
            <button
              key={`tpl-${item.id}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect("template", item);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              <LayoutTemplate className="size-4 text-muted-foreground shrink-0" />
              <span className="truncate">{item.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Recipients */}
      {searchResults?.recipients?.length > 0 && (
        <div className="mb-2">
          <p className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t("search.recipients", "Recipients")}
          </p>
          {searchResults.recipients.map((item) => (
            <button
              key={`rcp-${item.id}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect("recipient", item);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              <Mail className="size-4 text-muted-foreground shrink-0" />
              <div className="flex flex-col items-start min-w-0">
                <span className="truncate">{item.name || item.email}</span>
                <span className="text-xs text-muted-foreground truncate">
                  {item.envelopeTitle}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Folders */}
      {searchResults?.folders?.length > 0 && (
        <div className="mb-2">
          <p className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t("nav.folders", "Folders")}
          </p>
          {searchResults.folders.map((item) => (
            <button
              key={`fld-${item.id}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect("folder", item);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              <Folder className="size-4 text-muted-foreground shrink-0" />
              <span className="truncate">{item.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Tags */}
      {searchResults?.tags?.length > 0 && (
        <div className="mb-2">
          <p className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t("nav.tags", "Tags")}
          </p>
          {searchResults.tags.map((item) => (
            <button
              key={`tag-${item.id}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect("tag", item);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              <Tag className="size-4 shrink-0" style={{ color: item.color }} />
              <span className="truncate">{item.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Team Members */}
      {searchResults?.teamMembers?.length > 0 && (
        <div>
          <p className="px-2 py-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {t("search.teamMembers", "Team Members")}
          </p>
          {searchResults.teamMembers.map((item) => (
            <button
              key={`tm-${item.id}`}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                onSelect("teamMember", item);
              }}
              className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground cursor-pointer"
            >
              <Users className="size-4 text-muted-foreground shrink-0" />
              <div className="flex flex-col items-start min-w-0">
                <span className="truncate">{item.name}</span>
                <span className="text-xs text-muted-foreground truncate">
                  {item.email}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const AppSidebar = ({ ...props }) => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const handleLogout = useLogout();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useSidebar();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchInputRef = useRef(null);
  const sidebarContentRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Navigation items - memoized properly
  // Main navigation - available to all users
  const mainNavItems = useMemo(
    () => [
      {
        name: t("dashboard.title"),
        href: "/dashboard",
        icon: Home,
        keywords: ["home", "main", "overview"],
      },
      {
        name: t("envelopes.title"),
        href: "/envelopes",
        icon: FileText,
        keywords: ["documents", "contracts", "sign"],
      },
      {
        name: t("templates.title"),
        href: "/templates",
        icon: LayoutTemplate,
        keywords: ["reusable", "forms"],
      },
      {
        name: t("contacts.title", "Contacts"),
        href: "/contacts",
        icon: Users,
        keywords: ["people", "recipients", "address book"],
      },
      {
        name: t("folders.title"),
        href: "/folders",
        icon: Folder,
        keywords: ["organize", "directory"],
      },
      {
        name: t("tags.title"),
        href: "/tags",
        icon: Tag,
        keywords: ["labels", "categories"],
      },
      {
        name: t("reminders.title", "Reminders"),
        href: "/reminders",
        icon: Bell,
        keywords: ["remind", "follow up", "pending", "notifications"],
      },
    ],
    [t],
  );

  // Features - advanced signing features
  const allFeaturesNavItems = useMemo(
    () => [
      {
        name: t("bulkSend.title"),
        href: "/bulk-send",
        icon: Send,
        keywords: ["mass", "batch", "multiple"],
      },
      {
        name: t("signingLinks.title"),
        href: "/signing-links",
        icon: Link,
        keywords: ["direct", "url", "share"],
      },
      {
        name: t("inPerson.title"),
        href: "/in-person",
        icon: UserCheck,
        keywords: ["face", "person", "live"],
      },
      {
        name: t("clauseLibrary.title"),
        href: "/clause-library",
        icon: Library,
        keywords: ["clauses", "snippets", "text"],
      },
      {
        name: t("documentGeneration.title", "Document Generation"),
        href: "/document-generation",
        icon: FileOutput,
        keywords: ["generate", "template", "merge", "docx", "pdf"],
      },
      {
        name: t("nav.documentTools"),
        href: "/document-tools",
        icon: FileEdit,
        keywords: ["combine", "redact", "pdf", "merge", "sensitive"],
      },
      {
        name: t("analytics.title"),
        href: "/analytics",
        icon: BarChart2,
        keywords: ["reports", "stats", "metrics"],
      },
    ],
    [t],
  );

  const featuresNavItems = useMemo(
    () => filterNavItems(allFeaturesNavItems, user),
    [allFeaturesNavItems, user],
  );

  // Tools - filtered based on role (admin/owner only)
  const allToolsNavItems = useMemo(
    () => [
      {
        name: t("mergeKeys.title"),
        href: "/merge-keys",
        icon: FileCode2,
        keywords: ["variables", "placeholders", "merge"],
      },
      {
        name: t("apiKeys.title"),
        href: "/api-keys",
        icon: Key,
        keywords: ["api", "integration", "developer"],
      },
      {
        name: t("webhooks.title"),
        href: "/webhooks",
        icon: Webhook,
        keywords: ["webhooks", "events", "notifications", "callbacks"],
      },
      {
        name: t("integrations.title"),
        href: "/integrations",
        icon: Plug,
        keywords: ["zapier", "connect", "automate"],
      },
      {
        name: t("embed.title"),
        href: "/embed",
        icon: Code2,
        keywords: ["embed", "sdk", "integration", "iframe"],
      },
    ],
    [t],
  );

  const toolsNavItems = useMemo(
    () => filterNavItems(allToolsNavItems, user),
    [allToolsNavItems, user],
  );

  // Admin nav items - only visible to app owner (configured via OWNER_EMAIL env var)
  const adminNavItems = useMemo(() => {
    if (!user?.isOwner) return [];
    return [
      {
        name: t("nav.adminDashboard"),
        href: "/admin",
        icon: Shield,
        keywords: ["admin", "management", "system"],
      },
      {
        name: t("nav.userManagement"),
        href: "/admin/users",
        icon: Users,
        keywords: ["users", "manage", "features"],
      },

      {
        name: t("nav.organisations"),
        href: "/admin/organisations",
        icon: Building2,
        keywords: ["orgs", "plans", "features", "billing"],
      },
      {
        name: t("nav.customPlans"),
        href: "/admin/custom-plans",
        icon: Crown,
        keywords: ["custom", "plans", "features", "limits", "enterprise"],
      },
      {
        name: t("nav.manualInvoices"),
        href: "/admin/invoices",
        icon: FileText,
        keywords: ["invoices", "billing", "payments", "manual"],
      },
      {
        name: t("nav.pricing", "Pricing"),
        href: "/admin/pricing",
        icon: DollarSign,
        keywords: ["pricing", "prices", "usage", "rates", "billing"],
      },
      {
        name: t("nav.smsManagement"),
        href: "/admin/sms",
        icon: MessageSquare,
        keywords: ["sms", "messages", "twilio", "notifications"],
      },
      {
        name: t("nav.security"),
        href: "/admin/security",
        icon: Key,
        keywords: ["jwt", "keys", "rotation", "tokens", "security"],
      },
      {
        name: t("nav.knowledgeBase", "Knowledge Base"),
        href: "/admin/knowledge-base",
        icon: BookOpen,
        keywords: ["help", "docs", "articles", "documentation", "kb"],
      },
    ];
  }, [t, user?.isOwner]);

  // Account items - filtered based on role
  const allAccountNavItems = useMemo(
    () => [
      {
        name: t("usage.title"),
        href: "/usage",
        icon: BarChart3,
        keywords: ["statistics", "analytics", "reports"],
      },
      {
        name: t("common.settings"),
        href: "/settings",
        icon: Settings,
        keywords: ["preferences", "profile", "account"],
      },
    ],
    [t],
  );

  const accountNavItems = useMemo(
    () => filterNavItems(allAccountNavItems, user),
    [allAccountNavItems, user],
  );

  // Resources items - always visible
  // const resourceNavItems = useMemo(
  //   () => [
  //     {
  //       name: t("knowledgeBase.title"),
  //       href: "/knowledge-base",
  //       icon: BookOpen,
  //       keywords: ["help", "docs", "documentation", "faq", "support", "guide"],
  //     },
  //   ],
  //   [t],
  // );

  // All navigation items for search
  // const allNavItems = useMemo(
  //   () => [
  //     ...mainNavItems,
  //     ...featuresNavItems,
  //     ...toolsNavItems,
  //     ...adminNavItems,
  //     ...accountNavItems,
  //     ...resourceNavItems,
  //   ],
  //   [
  //     mainNavItems,
  //     featuresNavItems,
  //     toolsNavItems,
  //     adminNavItems,
  //     accountNavItems,
  //     resourceNavItems,
  //   ],
  // );

  // Global search functionality with debounce
  useEffect(() => {
    // Clear any pending search
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim() === "") {
      setSearchResults(null);
      setShowSearchResults(false);
      setIsSearching(false);
      return;
    }

    setIsSearching(true);
    setShowSearchResults(true);

    // Debounce API call by 300ms
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await searchApi.globalSearch(searchQuery.trim(), 5);
        setSearchResults(response.data?.data || response.data);
      } catch (error) {
        console.error("Search failed:", error);
        setSearchResults(null);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const handleSearchSelect = (type, item) => {
    switch (type) {
      case "envelope":
        navigate(`/envelopes/${item.id}`);
        break;
      case "template":
        navigate(`/templates/${item.id}`);
        break;
      case "recipient":
        navigate(`/envelopes/${item.envelopeId}`);
        break;
      case "folder":
        navigate(`/envelopes?folder=${item.id}`);
        break;
      case "tag":
        navigate(`/envelopes?tag=${item.id}`);
        break;
      case "teamMember":
        navigate(`/settings/team`);
        break;
      default:
        break;
    }
    setSearchQuery("");
    setShowSearchResults(false);
  };

  // Check if search has any results
  const hasSearchResults =
    searchResults &&
    (searchResults.envelopes?.length > 0 ||
      searchResults.templates?.length > 0 ||
      searchResults.recipients?.length > 0 ||
      searchResults.folders?.length > 0 ||
      searchResults.tags?.length > 0 ||
      searchResults.teamMembers?.length > 0);

  const userInitials = `${user?.firstName?.[0] || ""}${
    user?.lastName?.[0] || ""
  }`.toUpperCase();

  // Get plan display name from subscription
  const getPlanDisplayName = () => {
    const plan = user?.subscription?.plan || "free";
    const planMap = {
      free: t("billing.plans.free"),
      starter: t("billing.plans.starter"),
      professional: t("billing.plans.professional"),
      enterprise: t("billing.plans.enterprise"),
    };
    return planMap[plan] || t("billing.plans.free");
  };

  const isActive = (href) => {
    const { pathname } = location;
    // Normalize paths by removing trailing slashes for comparison
    const normalizedPathname =
      pathname.endsWith("/") && pathname !== "/"
        ? pathname.slice(0, -1)
        : pathname;
    const normalizedHref =
      href.endsWith("/") && href !== "/" ? href.slice(0, -1) : href;

    // Exact match for root pages to prevent false positives
    if (normalizedHref === "/dashboard" || normalizedHref === "/admin") {
      return normalizedPathname === normalizedHref;
    }

    // For other routes, check if pathname starts with href
    return (
      normalizedPathname === normalizedHref ||
      normalizedPathname.startsWith(`${normalizedHref}/`)
    );
  };

  return (
    <Sidebar collapsible="icon" {...props}>
      {/* Header with Logo */}
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              size="lg"
              // Expanded: auto height; the h-12 default can't fit the
              // lockup plus the plan name line
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[state=collapsed]:justify-center group-data-[state=expanded]:!h-auto group-data-[state=expanded]:py-2"
              tooltip={t("common.appName")}
              asChild
            >
              <NavLink to="/dashboard">
                {/* Collapsed: square icon. The sidebar is navy in both
                    themes, so the on-dark variants are forced. */}
                {/* rounded-[10.7%] matches the corner radius baked into the
                    icon artwork so the ring hugs the tile */}
                <LogoIcon
                  size="md"
                  surface="dark"
                  className="hidden group-data-[state=collapsed]:block !size-8 rounded-[10.7%] ring-1 ring-white/20"
                />
                {/* Expanded: full lockup with the plan name beneath */}
                <div className="flex flex-row items-center justify-end gap-4 text-left text-sm leading-tight group-data-[state=collapsed]:hidden">
                  <img
                    src={logoOnDark}
                    alt={t("common.appName")}
                    className="h-7 w-auto justify-self-start"
                  />
                  <span className="truncate text-xs leading-none text-slate-400 border-l border-l-slate-800 pl-3 h-6 flex items-center justify-start">
                    {getPlanDisplayName()}
                  </span>
                </div>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent ref={sidebarContentRef}>
        {/* Search - hidden when collapsed */}
        {state === "expanded" && (
          <SidebarGroup className="pb-0 pt-2">
            <SidebarGroupContent className="relative">
              <Label htmlFor="sidebar-search" className="sr-only">
                {t("common.search")}
              </Label>
              <div className="relative">
                <SidebarInput
                  ref={searchInputRef}
                  id="sidebar-search"
                  placeholder={t(
                    "search.placeholder",
                    "Search envelopes, templates...",
                  )}
                  className="pl-8"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => {
                    if (searchQuery) setShowSearchResults(true);
                  }}
                  onBlur={(e) => {
                    const resultsContainer =
                      e.currentTarget.parentElement?.querySelector(
                        "[data-search-results]",
                      );
                    if (resultsContainer?.contains(e.relatedTarget)) {
                      return;
                    }
                    setTimeout(() => setShowSearchResults(false), 150);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      setShowSearchResults(false);
                      setSearchQuery("");
                      e.currentTarget.blur();
                    }
                  }}
                />
                {isSearching ? (
                  <Loader2 className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 select-none animate-spin opacity-50" />
                ) : (
                  <Search className="pointer-events-none absolute left-2 top-1/2 size-4 -translate-y-1/2 select-none opacity-50" />
                )}
              </div>

              {/* Global Search Results Dropdown */}
              {showSearchResults && searchQuery && (
                <div
                  data-search-results
                  className="absolute top-full left-0 right-0 z-50 mt-1 rounded-md border bg-popover shadow-md max-h-100 overflow-y-auto"
                >
                  <SearchResultsContent
                    isSearching={isSearching}
                    hasSearchResults={hasSearchResults}
                    searchResults={searchResults}
                    onSelect={handleSearchSelect}
                    t={t}
                  />
                </div>
              )}
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* New Envelope Button - icon only when collapsed */}
        <SidebarGroup className="py-0">
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  onClick={() => navigate("/envelopes/new")}
                  tooltip={t("envelopes.newEnvelope")}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                >
                  <Plus className="size-4 shrink-0" />
                  <span className="group-data-[state=collapsed]:hidden">
                    {t("envelopes.newEnvelope")}
                  </span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupLabel>{t("common.navigation")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {mainNavItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.href)}
                    tooltip={item.name}
                  >
                    <NavLink to={item.href}>
                      <item.icon className="shrink-0" />
                      <span className="group-data-[state=collapsed]:hidden">
                        {item.name}
                      </span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Features - only visible if user has access to any features */}
        {featuresNavItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>{t("common.features")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {featuresNavItems.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.href)}
                      tooltip={item.name}
                    >
                      <NavLink to={item.href}>
                        <item.icon className="shrink-0" />
                        <span className="group-data-[state=collapsed]:hidden">
                          {item.name}
                        </span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Tools - only visible if user has access to any tools */}
        {toolsNavItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel>{t("common.tools")}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {toolsNavItems.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.href)}
                      tooltip={item.name}
                    >
                      <NavLink to={item.href}>
                        <item.icon className="shrink-0" />
                        <span className="group-data-[state=collapsed]:hidden">
                          {item.name}
                        </span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Admin (only visible to admin/superadmin) */}
        {adminNavItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-amber-600">
              {t("common.admin")}
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {adminNavItems.map((item) => (
                  <SidebarMenuItem key={item.href}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive(item.href)}
                      tooltip={item.name}
                    >
                      <NavLink to={item.href}>
                        <item.icon className="shrink-0" />
                        <span className="group-data-[state=collapsed]:hidden">
                          {item.name}
                        </span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Account */}
        <SidebarGroup>
          <SidebarGroupLabel>{t("common.account")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {accountNavItems.map((item) => (
                <SidebarMenuItem key={item.href}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive(item.href)}
                    tooltip={item.name}
                  >
                    <NavLink to={item.href}>
                      <item.icon className="shrink-0" />
                      <span className="group-data-[state=collapsed]:hidden">
                        {item.name}
                      </span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Resources */}
        <SidebarGroup>
          <SidebarGroupLabel>{t("common.resources")}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActive("/knowledge-base")}
                  tooltip={t("knowledgeBase.title")}
                >
                  <NavLink to="/knowledge-base">
                    <BookOpen className="shrink-0" />
                    <span className="group-data-[state=collapsed]:hidden">
                      {t("knowledgeBase.title")}
                    </span>
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip={t("nav.apiDocumentation", "API Documentation")}
                >
                  <a
                    href="/api-docs.html"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileCode className="shrink-0" />
                    <span className="group-data-[state=collapsed]:hidden flex items-center gap-1">
                      {t("nav.apiDocumentation", "API Documentation")}
                      <ExternalLink className="h-3 w-3 opacity-50" />
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  tooltip={t(
                    "nav.embedSdkDocumentation",
                    "Embed SDK Documentation",
                  )}
                >
                  <a
                    href="/embed-docs.html"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Code2 className="shrink-0" />
                    <span className="group-data-[state=collapsed]:hidden flex items-center gap-1">
                      {t(
                        "nav.embedSdkDocumentation",
                        "Embed SDK Documentation",
                      )}
                      <ExternalLink className="h-3 w-3 opacity-50" />
                    </span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* Footer with User Menu */}
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground group-data-[state=collapsed]:justify-center"
                >
                  <Avatar className="h-8 w-8 rounded-lg shrink-0">
                    {user?.avatarUrl ? (
                      <AvatarImage
                        src={user.avatarUrl}
                        alt={`${user?.firstName} ${user?.lastName}`}
                        className="rounded-lg"
                      />
                    ) : null}
                    <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight group-data-[state=collapsed]:hidden">
                    <span className="truncate font-semibold">
                      {user?.firstName} {user?.lastName}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user?.email}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto size-4 group-data-[state=collapsed]:hidden" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side={state === "collapsed" ? "right" : "bottom"}
                align="end"
                sideOffset={4}
              >
                <DropdownMenuLabel className="p-0 font-normal">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-lg">
                      {user?.avatarUrl ? (
                        <AvatarImage
                          src={user.avatarUrl}
                          alt={`${user?.firstName} ${user?.lastName}`}
                          className="rounded-lg"
                        />
                      ) : null}
                      <AvatarFallback className="rounded-lg bg-primary text-primary-foreground">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {user?.firstName} {user?.lastName}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {user?.organisation?.name || user?.email}
                      </span>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {getUserRoleDisplay(user)}
                    </Badge>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {canAccessPage(user, "/usage") && (
                  <>
                    <DropdownMenuGroup>
                      <DropdownMenuItem onClick={() => navigate("/usage")}>
                        <Sparkles className="mr-2 h-4 w-4" />
                        {t("usage.title")}
                      </DropdownMenuItem>
                    </DropdownMenuGroup>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuGroup>
                  <DropdownMenuItem
                    onClick={() => navigate("/settings/profile")}
                  >
                    <BadgeCheck className="mr-2 h-4 w-4" />
                    {t("settings.profile.title")}
                  </DropdownMenuItem>
                  {canAccessPage(user, "/settings/billing") && (
                    <DropdownMenuItem
                      onClick={() => navigate("/settings/billing")}
                    >
                      <CreditCard className="mr-2 h-4 w-4" />
                      {t("billing.title")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem
                    onClick={() => navigate("/settings/notifications")}
                  >
                    <Bell className="mr-2 h-4 w-4" />
                    {t("common.notifications")}
                  </DropdownMenuItem>
                </DropdownMenuGroup>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
};

export default AppSidebar;
